import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';

import { useGetKeySourcesStats } from '../../../../../shared/api/hooks/monitor/data/sources/UseGetKeySourcesStats';
import { useSourcesStatsShare, SourceStatsWithShare } from '../../../../sources/hooks/UseSourcesStatsShare';
import { ChatType } from '../../../../../shared/types/monitor/ChatType';
import { useFilters } from '../../../../../shared/contexts/filters/FiltersContext';
import { DropdownItemData } from '../../../../../shared/components/dropdown-item/DropdownItem';
import {
  getDomainsOptions,
  findSelectedDomain,
} from '../../../../prompt/preview/components/stats/helpers/StatsHelper';
import { createCustomColumn, modelTemplate } from '../../../../../shared/helpers/ColumnHelper';
import useIsMobile from '../../../../../shared/hooks/global/UseIsMobile';
import { MetadataBadge } from '../../../../../shared/components/metadata-badge/MetadataBadge';
import { getVerifiedBrandStatus, isFirmMentionedInBrands } from '../../../../sources/helpers/SourcesHelper';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import CustomLink from '../../../../../shared/components/custom-link/CustomLink';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import useExport from '../../../../../shared/hooks/global/UseExport';
import { LinksData } from '../../../../prompt/preview/components/stats/models/StatsModel';

import './KeySources.scss';

interface KeySourcesProps {
  projectId: number;
  chatType: ChatType | null;
  startDate: Date | null;
  endDate: Date | null;
  monitorPromptIds?: number[];
  filterMonitorTypes?: string[];
  domains: string[];
  exportSources: () => Promise<void>;
  isDemo: boolean;
  /**
   * Resolved brand label used by the "mentions" column (gated by `hasAdvancedFeatures`).
   * Use `firmName.startsWith(TAG_NAME) ? firmNameLabel : firmName` at the call site.
   */
  firmName?: string;
  tags?: TagsResponse[];
  /** SOURCES_STATS_ADVANCED — gates the "mentions" column. */
  hasAdvancedFeatures?: boolean;
}

const DEFAULT_PAGE_SIZE = 20;

const KeySources: React.FC<KeySourcesProps> = ({
  projectId,
  chatType,
  startDate,
  endDate,
  monitorPromptIds,
  filterMonitorTypes,
  domains,
  exportSources,
  isDemo,
  firmName,
  tags = [],
  hasAdvancedFeatures = false,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { domain, setDomain } = useFilters();
  const { canExport } = useExport();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [first, setFirst] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  // ---------------------------------------------------------------------------
  // TABLE — server-side paginated sources (key-sources-stats fast endpoint)
  // domain must be `[domain]` (array) or `undefined` (NOT null — null disables the query).
  // ---------------------------------------------------------------------------
  const { sourcesStats, isLoading: isSourcesLoading } = useGetKeySourcesStats(
    projectId,
    chatType,
    startDate,
    endDate,
    page,
    pageSize,
    domain ? [domain] : undefined,
    undefined,
    monitorPromptIds,
    filterMonitorTypes
  );

  const sourcesWithShare = useSourcesStatsShare(sourcesStats);

  const tableSources = useMemo(() => sourcesWithShare?.sources ?? [], [sourcesWithShare]);

  // Use pagination.totalItems = COUNT(DISTINCT source) (distinct source rows the table renders).
  // NOT totalCount = COUNT(*) (total citation rows) — that is ~3x higher and would produce empty
  // trailing pages in the paginator.
  const totalRecords = useMemo(() => sourcesStats.pagination?.totalItems ?? 0, [sourcesStats]);

  const handlePage = (event: { first: number; rows: number }) => {
    const newPage = Math.floor(event.first / event.rows) + 1;
    if (newPage !== page || event.rows !== pageSize) {
      setPage(newPage);
      setPageSize(event.rows);
      setFirst(event.first);
    }
  };

  const handleDomainChange = (name: string) => {
    if (name === t('allDomains')) {
      setDomain(null);
    } else {
      setDomain(name);
    }
    setPage(1);
    setFirst(0);
  };

  const domainsOptions: DropdownItemData<string>[] = useMemo(
    () => getDomainsOptions(domains, domain),
    [domains, domain]
  );

  const selectedOption = useMemo(
    () => findSelectedDomain(domain, domainsOptions),
    [domain, domainsOptions]
  );

  const handleExportSources = async () => {
    if (isDownloading || !canExport) return;
    setIsDownloading(true);
    try {
      await exportSources();
    } finally {
      setTimeout(() => setIsDownloading(false), 500);
    }
  };

  return (
    <div className="page-box__stats">
      <div className="stats__panel">
        <div id="key-sources">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('linkTracking')}
              <CustomTooltip tooltipText="linkTrackingTooltip" />
            </div>
            <div className="page-box__source-filter">
              <Dropdown
                value={selectedOption}
                placeholder={t('selectDomain')}
                onChange={(e) => handleDomainChange(e.value.name)}
                options={domainsOptions}
                className="dropdown"
                optionLabel="name"
                optionValue="type"
                filter
              />
              {/* Przy pustej tabeli nie ma czego eksportować. */}
              {!isDemo && totalRecords > 0 && (
                <>
                  <Button
                    id="btn-key-sources-export"
                    className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
                    onClick={handleExportSources}
                  >
                    {isDownloading ? (
                      <span className="ico--loader ico--xs" />
                    ) : (
                      <span
                        className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`}
                      />
                    )}
                  </Button>
                  <CustomTooltip
                    tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadSources'}
                    targetRef="btn-key-sources-export"
                  />
                </>
              )}
            </div>
          </div>

          {isSourcesLoading ? (
            <Skeleton className="skeleton--table">
              <span />
              <span />
              <span />
              <span />
              <span />
            </Skeleton>
          ) : (
            <DataTable
              value={tableSources}
              stripedRows
              paginator
              lazy
              paginatorClassName="pagination"
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              rows={pageSize}
              first={first}
              totalRecords={totalRecords}
              onPage={handlePage}
              rowsPerPageOptions={[10, 20, 50]}
              tableStyle={{ minWidth: '40rem', tableLayout: 'auto' }}
              tableClassName="table"
              className="table-wrapper table-wrapper--stack"
              responsiveLayout="stack"
              currentPageReportTemplate={t('rowsNumber') + ':'}
              emptyMessage={() => <EmptyPanel />}
              dataKey="source"
            >
              <Column
                field="index"
                header={t('position')}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell table__cell--center table__cell--stack-rank"
                style={{ width: '50px' }}
                // PrimeReact's `rowIndex` in lazy+paginator mode is already the GLOBAL index
                // (first + local index), so do NOT add `first` again — that double-counts and
                // shifts the numbering on every page after the first.
                body={(_rowData: SourceStatsWithShare, { rowIndex }: { rowIndex: number }) =>
                  `#${rowIndex + 1}`
                }
              />
              <Column
                field="source"
                bodyClassName="table__cell--stack-title"
                header={t('url')}
                headerClassName="table__header"
                style={{ maxWidth: '220px' }}
                body={(rowData: SourceStatsWithShare) => (
                  <div className="source-data">
                    <span
                      className="source-data__icon"
                      style={{
                        backgroundImage: `url("https://favicon.is/${rowData.source}")`,
                      }}
                    />
                    <div className="source-data__details source-data__details--basic">
                      {rowData.title && (
                        <span className="source-data__title text-ellipsis">{rowData.title}</span>
                      )}
                      <span className="text-ellipsis">
                        <CustomLink url={rowData.source} target="_blank" />
                      </span>
                    </div>
                  </div>
                )}
              />
              <Column
                field="model"
                header={t('model')}
                headerClassName="table__header"
                style={{ width: '200px' }}
                body={(rowData: SourceStatsWithShare) =>
                  modelTemplate(rowData as unknown as LinksData)
                }
              />
              {firmName && hasAdvancedFeatures && (
                <Column
                  field="isVerified"
                  header={t('mentions')}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell table__cell--center"
                  style={{ width: '120px' }}
                  body={(rowData: SourceStatsWithShare, { rowIndex }: { rowIndex: number }) => {
                    const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                      rowData.isVerified,
                      isFirmMentionedInBrands(rowData.brands, firmName, tags),
                      firmName,
                      t
                    );
                    return (
                      <MetadataBadge
                        variant={variant}
                        text={statusText}
                        tooltipText={tooltipText}
                        uniqueId={`key-sources-brand-tooltip-${rowIndex}`}
                      />
                    );
                  }}
                />
              )}
              {createCustomColumn({
                plainHeader: isMobile,
                field: 'count',
                body: (rowData: SourceStatsWithShare) => `${rowData.share.toFixed(1)}%`,
                headerName: 'coverage',
                tooltip: t('coverageTooltip'),
                headerClassName: 'table__header table__header--center',
                bodyClassName: 'table__cell table__cell--center',
                style: { width: '50px' },
              })}
            </DataTable>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeySources;
